import { Button } from "@/components/ui/button";
import { Copy, Hash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable badge that displays the unified quote/ride/invoice reference
 * (e.g. "SD-2026-017") with a built-in "Copier la référence" action.
 *
 * Renders nothing when no reference is available, so callers can drop it
 * inline without ternaries.
 */
export default function ReferenceBadge({
  value,
  size = "md", // "xs" | "sm" | "md"
  variant = "default", // "default" | "muted"
  testid,
  className = "",
}) {
  if (!value) return null;

  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Référence copiée");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Référence copiée");
    }
  };

  const sizeCls =
    size === "xs"
      ? "text-[10px] px-1.5 h-5 gap-1"
      : size === "sm"
      ? "text-xs px-2 h-6 gap-1"
      : "text-sm px-2.5 h-7 gap-1.5";
  const tone =
    variant === "muted"
      ? "bg-zinc-800/60 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
      : "bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20";

  return (
    <button
      type="button"
      data-testid={testid || `ref-badge-${value}`}
      onClick={copy}
      title="Copier la référence"
      aria-label={`Référence ${value} — cliquer pour copier`}
      className={`inline-flex items-center border rounded font-mono font-medium transition-colors ${sizeCls} ${tone} ${className}`}
    >
      <Hash size={size === "xs" ? 10 : size === "sm" ? 11 : 13} weight="bold" className="shrink-0 opacity-70" />
      <span>{value}</span>
      <Copy size={size === "xs" ? 10 : 11} weight="bold" className="shrink-0 opacity-60" />
    </button>
  );
}
